import React from 'react';
import styled, { css } from 'styled-components';

import useTranslation from 'hooks/use-translation';
import type { TFunction } from 'hooks/use-translation';
import { InlineBanner } from 'src/components/inline-banner';
import { Button } from 'src/components/core';
import { ShieldCheck } from 'src/svg/shield-check';

import { usePhoneVerificationCta } from './use-phone-verification-cta';
import type { BannerError, CtaState } from './use-phone-verification-cta';

/**
 * Persistent "Verify your phone number" banner shown at the top of the My Account form. Reuses
 * `InlineBanner severity='warning'` (already the design's pale yellow), overriding its built-in
 * warning-triangle icon with `ShieldCheck` via `InlineBanner`'s `icon` prop. Renders `null` (no
 * reserved space) when there's nothing to verify or report.
 */
export function PhoneVerificationCta(): JSX.Element | null {
  const { t } = useTranslation();
  const { isVisible, isSending, cta, bannerError, onVerifyClick } = usePhoneVerificationCta();

  if (!isVisible) {
    return null;
  }

  if (bannerError) {
    // Buttonless -- deliberately does NOT pass `contentStyles` here. `InlineBanner`'s own default
    // `max-width: calc(100% - 36px)` on its `Content` reserves the trailing space that would
    // otherwise be occupied by a button, giving the text right padding instead of running flush
    // to the container's border. The `leg_one` variant below overrides that to `100%` because its
    // `Row`/`ButtonWrapper` already reserve their own space via the button itself.
    return (
      <InlineBanner severity='warning' border icon={<ShieldCheck />}>
        <Subtitle>{bannerErrorMessage(bannerError, t)}</Subtitle>
      </InlineBanner>
    );
  }

  if (cta?.kind === 'verified_other') {
    // Informational only -- there is nothing to action here, so this deliberately renders no
    // button. It's a plain `Subtitle` (no `Row`/`ButtonWrapper`), same shape as the error variants
    // above, so the `space-between` row layout never has to cope with a missing trailing button.
    // See the `bannerError` branch above for why `contentStyles` is intentionally omitted.
    return (
      <InlineBanner severity='warning' border icon={<ShieldCheck />}>
        <Subtitle>{verifiedOtherMessage(cta, t)}</Subtitle>
      </InlineBanner>
    );
  }

  if (cta?.kind !== 'leg_one') {
    return null;
  }

  const title = t('account.phoneVerification.title', 'Verify your phone number');
  const subtitle = t('account.phoneVerification.subtitle', 'Help us keep your account safe and secure.');
  const ctaLabel = t('account.phoneVerification.cta', 'Verify Now');

  return (
    <InlineBanner severity='warning' border icon={<ShieldCheck />} contentStyles={contentStyles}>
      <Row>
        <TextBlock>
          <TextContent>
            <Title>{title}</Title>
            <Subtitle>{subtitle}</Subtitle>
          </TextContent>
        </TextBlock>
        <ButtonWrapper>
          <Button
            aria-label={ctaLabel}
            onClick={() => void onVerifyClick()}
            loading={isSending}
            size='small'
            type='button'
            variant='outline'
          >
            {ctaLabel}
          </Button>
        </ButtonWrapper>
      </Row>
    </InlineBanner>
  );
}

function verifiedOtherMessage(cta: Extract<CtaState, { kind: 'verified_other' }>, t: TFunction): string {
  // `ownerEmailHint` is rendered verbatim, never re-masked -- interpolated via `t`'s
  // `{{placeholder}}` support (see `src/account/unsubscribe.tsx` for existing precedent), rather
  // than baked into the fallback string.
  return cta.ownerEmailHint
    ? t('account.phoneVerification.conflictWithHint', {
        defaultValue:
          'This number belongs to {{ownerEmailHint}}. Please log in with that account or change your number below.',
        ownerEmailHint: cta.ownerEmailHint,
      })
    : t(
        'account.phoneVerification.conflict',
        'This number belongs to another account. Please log in with that account or change your number below.'
      );
}

function bannerErrorMessage(bannerError: BannerError, t: TFunction): string {
  switch (bannerError.kind) {
    case 'rate_limited':
      return t('account.phoneVerification.rateLimited', "You've made too many attempts. Please try again later.");
    case 'invalid_number':
      return t(
        'account.phoneVerification.invalidNumber',
        "We can't send a code to the number on file. Please update it below."
      );
    case 'unavailable':
    default:
      return t('account.phoneVerification.unavailable', 'Phone verification is unavailable right now.');
  }
}

const contentStyles = css`
  max-width: 100%;
`;

const Row = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
`;

const TextBlock = styled.div`
  align-items: flex-start;
  display: flex;
`;

const TextContent = styled.div``;

const Title = styled.strong`
  color: var(--dcm-phone-verification-cta-title-color, #594f00);
  display: block;
  font-weight: bold;
`;

const Subtitle = styled.p`
  color: var(--dcm-phone-verification-cta-subtitle-color, #75725b);
  margin: 2px 0 0;
`;

const ButtonWrapper = styled.div`
  flex-shrink: 0;
  margin-left: 16px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-left: 0;
    width: 100%;

    button {
      width: 100%;
    }
  }
`;
